import React, { useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import Modal from "../Model";
import FeedPreview from "./FeedPreview";
import SectionForm from "./SectionForm";
import {
  GET_HOME_LAYOUT, PREVIEW_HOME_FEED, SAVE_HOME_SECTION, DELETE_HOME_SECTION,
  REORDER_HOME_SECTIONS, PUBLISH_HOME_LAYOUT, ARCHIVE_HOME_LAYOUT, SAVE_HOME_LAYOUT,
} from "./graphql";
import {
  SECTION_TYPES, AUDIENCES, STATUS_STYLES, formatDate, toLocalInput, fromLocalInput,
} from "./labels";

const sectionInput = (s, overrides = {}) => ({
  id: s.id,
  layoutId: s.layoutId,
  type: s.type,
  title: s.title,
  subtitle: s.subtitle,
  badge: s.badge,
  cardStyle: s.cardStyle,
  collectionId: s.collectionId,
  config: s.config,
  visibleCount: s.visibleCount,
  seeAllLabel: s.seeAllLabel,
  audience: s.audience,
  startAt: s.startAt,
  endAt: s.endAt,
  isActive: s.isActive,
  minAppVersion: s.minAppVersion,
  ...overrides,
});

const Toggle = ({ on, onChange, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    aria-label={label}
    onClick={onChange}
    className={`relative w-10 h-6 rounded-full transition-colors ${on ? "bg-green-600" : "bg-gray-300"}`}
  >
    <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${on ? "translate-x-4" : ""}`} />
  </button>
);

const LayoutSettings = ({ layout, onClose, onSaved }) => {
  const [name, setName] = useState(layout.name);
  const [hints, setHints] = useState((layout.searchHints || []).join("\n"));
  const [startAt, setStartAt] = useState(toLocalInput(layout.startAt));
  const [endAt, setEndAt] = useState(toLocalInput(layout.endAt));
  const [error, setError] = useState("");
  const [save, { loading }] = useMutation(SAVE_HOME_LAYOUT);
  const submit = async (e) => {
    e.preventDefault();
    const res = await save({
      variables: {
        data: {
          id: layout.id,
          name,
          gender: layout.gender,
          startAt: fromLocalInput(startAt),
          endAt: fromLocalInput(endAt),
          theme: layout.theme,
          searchHints: hints.split("\n").map((h) => h.trim()).filter(Boolean),
        },
      },
    });
    const out = res.data?.saveHomeLayout;
    if (out?.status !== "SUCCESS") { setError(out?.message || "Could not save"); return; }
    onSaved();
  };
  const input = "border rounded-md px-3 py-2 text-sm w-full";
  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      <h2 className="text-lg font-semibold">Layout settings</h2>
      <label className="flex flex-col gap-1 text-sm"><span className="font-medium">Name</span><input className={input} value={name} onChange={(e) => setName(e.target.value)} /></label>
      <label className="flex flex-col gap-1 text-sm">
        <span className="font-medium">Search suggestions (one per line)</span>
        <textarea rows={4} className={input} value={hints} onChange={(e) => setHints(e.target.value)} />
        <span className="text-xs text-gray-500">They rotate in the search box: "Search for Diwali facial".</span>
      </label>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <label className="flex flex-col gap-1 text-sm"><span className="font-medium">Live from (optional)</span><input type="datetime-local" className={input} value={startAt} onChange={(e) => setStartAt(e.target.value)} /></label>
        <label className="flex flex-col gap-1 text-sm"><span className="font-medium">Live until (optional)</span><input type="datetime-local" className={input} value={endAt} onChange={(e) => setEndAt(e.target.value)} /></label>
      </div>
      <p className="text-xs text-gray-500">Give a festival layout dates: while its window is open it replaces the default layout for this tab, and the default returns automatically afterwards.</p>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} className="px-4 py-2 rounded-md bg-gray-100">Cancel</button>
        <button type="submit" disabled={loading} className="px-5 py-2 rounded-md bg-[#FBB033] font-semibold">{loading ? "Saving…" : "Save"}</button>
      </div>
    </form>
  );
};

const LayoutEditor = ({ layoutId, collections, canEdit, onBack, onChanged }) => {
  const [audience, setAudience] = useState("NEW");
  const [editing, setEditing] = useState(null); // section object, {} for new, or null
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [notice, setNotice] = useState("");
  const layoutQuery = useQuery(GET_HOME_LAYOUT, { variables: { id: layoutId }, fetchPolicy: "network-only" });
  const previewQuery = useQuery(PREVIEW_HOME_FEED, { variables: { layoutId, audience }, fetchPolicy: "network-only" });
  const [saveSection] = useMutation(SAVE_HOME_SECTION);
  const [deleteSection] = useMutation(DELETE_HOME_SECTION);
  const [reorder] = useMutation(REORDER_HOME_SECTIONS);
  const [publish, { loading: publishing }] = useMutation(PUBLISH_HOME_LAYOUT);
  const [archive] = useMutation(ARCHIVE_HOME_LAYOUT);

  const layout = layoutQuery.data?.getHomeLayout?.layout;
  const sections = layout?.sections || [];

  const refresh = () => {
    layoutQuery.refetch();
    previewQuery.refetch();
    onChanged();
  };
  const report = (out, okText) => {
    if (out?.status === "SUCCESS") { setNotice(okText); setTimeout(() => setNotice(""), 2500); refresh(); return true; }
    // eslint-disable-next-line no-alert
    window.alert(out?.message || "Something went wrong");
    return false;
  };

  const move = async (index, delta) => {
    const ids = sections.map((s) => s.id);
    const j = index + delta;
    if (j < 0 || j >= ids.length) return;
    [ids[index], ids[j]] = [ids[j], ids[index]];
    const res = await reorder({ variables: { layoutId, sectionIds: ids } });
    report(res.data?.reorderHomeSections, "Order saved");
  };
  const toggle = async (s) => {
    const res = await saveSection({ variables: { data: sectionInput(s, { isActive: !s.isActive }) } });
    report(res.data?.saveHomeSection, s.isActive ? "Section turned off" : "Section turned on");
  };
  const remove = async (s) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove "${s.title || SECTION_TYPES[s.type]?.label}" from this layout?`)) return;
    const res = await deleteSection({ variables: { id: s.id } });
    report(res.data?.deleteHomeSection, "Section removed");
  };
  const doPublish = async () => {
    const live = layout.status === "LIVE";
    // eslint-disable-next-line no-alert
    if (!window.confirm(live ? "Publish your changes to the app now?" : `Make "${layout.name}" live in the app?${layout.startAt || layout.endAt ? "" : " It replaces the current default layout for this tab."}`)) return;
    const res = await publish({ variables: { id: layout.id } });
    report(res.data?.publishHomeLayout, "Published");
  };
  const doArchive = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Take this layout out of the app? Customers will see another live layout, or the old home if none is live.")) return;
    const res = await archive({ variables: { id: layout.id } });
    report(res.data?.archiveHomeLayout, "Archived");
  };

  if (layoutQuery.loading && !layout) return <p className="text-sm text-gray-500">Loading layout…</p>;
  if (!layout) return <p className="text-sm text-red-600">{layoutQuery.data?.getHomeLayout?.message || "Layout not found"}</p>;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3 bg-white rounded-lg border p-3">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="px-3 py-1.5 rounded-md bg-gray-100 text-sm">← All layouts</button>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold">{layout.name}</h2>
              <span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_STYLES[layout.status]}`}>{layout.status.toLowerCase()}</span>
            </div>
            <p className="text-xs text-gray-500">
              {layout.gender === "Female" ? "Salon" : "Bridal"} tab
              {layout.startAt || layout.endAt ? ` · ${formatDate(layout.startAt) || "now"} → ${formatDate(layout.endAt) || "no end"}` : " · default layout"}
              {layout.publishedAt ? ` · version ${layout.version}, published ${formatDate(layout.publishedAt)}` : " · never published"}
            </p>
          </div>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <button onClick={() => setSettingsOpen(true)} className="px-3 py-2 rounded-md bg-gray-100 text-sm">Settings</button>
            {layout.status === "LIVE" && <button onClick={doArchive} className="px-3 py-2 rounded-md bg-gray-100 text-sm">Take offline</button>}
            <button onClick={doPublish} disabled={publishing} className="px-4 py-2 rounded-md bg-green-600 text-white text-sm font-semibold disabled:opacity-60">
              {layout.status === "LIVE" ? "Publish changes" : "Publish"}
            </button>
          </div>
        )}
      </div>
      {notice && <div className="rounded-md bg-green-50 text-green-800 text-sm px-3 py-2">{notice}</div>}
      {layout.status === "LIVE" && (
        <p className="text-xs text-amber-700 bg-amber-50 rounded-md px-3 py-2">
          This layout is live. Section changes reach the app straight away; Publish changes records a new version.
        </p>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-6 items-start">
        <div className="bg-white rounded-lg border">
          <div className="flex items-center justify-between p-3 border-b">
            <p className="text-sm text-gray-600">{sections.length} sections, top to bottom as they appear in the app</p>
            {canEdit && <button onClick={() => setEditing({})} className="px-4 py-2 bg-[#FBB033] rounded-md text-sm font-semibold">+ Add section</button>}
          </div>
          <ul className="divide-y">
            {sections.map((s, i) => {
              const meta = SECTION_TYPES[s.type] || { label: s.type };
              return (
                <li key={s.id} className={`flex items-center gap-3 px-3 py-2.5 ${s.isActive ? "" : "opacity-50"}`}>
                  {canEdit && (
                    <div className="flex flex-col">
                      <button onClick={() => move(i, -1)} disabled={i === 0} className="text-xs px-1.5 rounded hover:bg-gray-100 disabled:opacity-30" aria-label="Move up">▲</button>
                      <button onClick={() => move(i, 1)} disabled={i === sections.length - 1} className="text-xs px-1.5 rounded hover:bg-gray-100 disabled:opacity-30" aria-label="Move down">▼</button>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-sm">{s.title || meta.label}</span>
                      <span className="text-[11px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">{meta.label}</span>
                      {meta.personal && <span className="text-[11px] px-1.5 py-0.5 rounded bg-purple-50 text-purple-700">personal</span>}
                      {s.audience !== "ALL" && <span className="text-[11px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">{AUDIENCES[s.audience].split(" (")[0]}</span>}
                      {(s.startAt || s.endAt) && <span className="text-[11px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">scheduled</span>}
                    </div>
                    <p className="text-xs text-gray-500 truncate">
                      {s.collectionName ? `${s.collectionName} · ` : ""}
                      {["SERVICE_RAIL", "BOOK_AGAIN", "MOST_BOOKED", "CATEGORY_GRID", "COUPONS"].includes(s.type) ? `${s.visibleCount} on home` : ""}
                      {s.startAt || s.endAt ? ` · ${formatDate(s.startAt) || "now"} → ${formatDate(s.endAt) || "no end"}` : ""}
                    </p>
                  </div>
                  {canEdit && (
                    <div className="flex items-center gap-2">
                      <Toggle on={s.isActive} onChange={() => toggle(s)} label={`Show ${s.title || meta.label}`} />
                      <button onClick={() => setEditing(s)} className="text-sm px-2 py-1 rounded hover:bg-gray-100">Edit</button>
                      <button onClick={() => remove(s)} className="text-sm px-2 py-1 rounded hover:bg-red-50 text-red-600">Remove</button>
                    </div>
                  )}
                </li>
              );
            })}
            {sections.length === 0 && <li className="px-3 py-8 text-center text-sm text-gray-500">No sections yet. Add the first one.</li>}
          </ul>
        </div>

        <div className="flex flex-col gap-3 xl:sticky xl:top-4">
          <div className="flex items-center justify-between bg-white rounded-lg border px-3 py-2">
            <span className="text-sm font-medium">Preview</span>
            <select value={audience} onChange={(e) => setAudience(e.target.value)} className="border rounded-md px-2 py-1 text-xs">
              <option value="NEW">As a new customer</option>
              <option value="REPEAT">As a repeat customer</option>
            </select>
          </div>
          <FeedPreview
            feed={previewQuery.data?.previewHomeFeed?.feed}
            loading={previewQuery.loading}
            error={previewQuery.data?.previewHomeFeed?.status === "ERROR" ? previewQuery.data.previewHomeFeed.message : null}
          />
          <p className="text-xs text-gray-400">Personal sections (live booking, rate, book again) need a real customer, so they stay empty here.</p>
        </div>
      </div>

      {editing && (
        <Modal onClose={() => setEditing(null)} disableOutsideClick>
          <SectionForm
            layoutId={layout.id}
            layoutGender={layout.gender}
            section={editing.id ? editing : null}
            collections={collections}
            onClose={() => setEditing(null)}
            onSaved={() => { setEditing(null); report({ status: "SUCCESS" }, "Section saved"); }}
          />
        </Modal>
      )}
      {settingsOpen && (
        <Modal onClose={() => setSettingsOpen(false)} disableOutsideClick>
          <LayoutSettings layout={layout} onClose={() => setSettingsOpen(false)} onSaved={() => { setSettingsOpen(false); report({ status: "SUCCESS" }, "Settings saved"); }} />
        </Modal>
      )}
    </div>
  );
};

export default LayoutEditor;
